import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { guard } from 'lit/directives/guard.js';
import { actions, events, Overlay } from "@omegagrid/core";
import { msg } from '@omegagrid/localize';
import { Code, CodeDiffEditor } from "@omegagrid/code";
import { Toolbar, ToolbarItem } from "@omegagrid/toolbar";
import { TreeDropdown, TreeEvent } from "@omegagrid/tree";
import { MarkdownLinkEvent, MarkdownEditor as OgMarkdownEditor } from "@omegagrid/markdown";
import { MarkdownEditorAdapter, MarkdownEditorOptions, MarkdownImage, MarkdownHistoryAdapter } from "./markdownEditorAdapter";
import { style } from "./markdownEditor.style";
import { AppComponent } from "../component";
import { HistoryItem, Saveable } from "../types";
import { fixMarkdown } from "../helpers";
import rp from "../global";
import type { SettingsChangeEvent } from "../container/settings";
import { createMonacoOptions, getEditorSettings, isEditorSetting } from "./editorSettings";
import "./editorSettingsMenu";

@customElement('rp-markdown-editor')
export class MarkdownEditor extends AppComponent implements Saveable {

	static styles = [style];

	@property({type: Object})
	adapter: MarkdownEditorAdapter<MarkdownEditor>;

	@query('og-overlay')
	loader: Overlay;

	@query('og-markdown-editor')
	markdown: OgMarkdownEditor;

	@query('og-code')
	code: Code;

	@query('og-code-diff')
	diffEditor: CodeDiffEditor;

	@query('og-toolbar')
	toolbar: Toolbar;

	@query('#ddHistory')
	historyDropdown: TreeDropdown;

	@state()
	toolbarItems: ToolbarItem[] = [];

	@state()
	history = false;

	@state()
	spellcheck = false;

	@state()
	sourceMode = false;

	@state()
	currentLiveItem: HistoryItem;

	@state()
	currentHistoryItem: HistoryItem;

	@state()
	historyAdapter: MarkdownHistoryAdapter;

	@state()
	diffIdz: number;

	markdownImages: MarkdownImage[] = [];

	protected options: MarkdownEditorOptions;
	private _pendingSave = false;
	private _saving = false;
	private _loading = false;
	private _editing = false;
	private _content: string;
	private _editorSettings = getEditorSettings();

	/** Monaco options of the source view: the user's editor settings (gear menu) on top of the markdown defaults */
	get monacoOptions(): Record<string, unknown> {
		return {
			tabSize: 4,
			insertSpaces: false,
			readOnly: !!this.options?.readOnly,
			...createMonacoOptions(this._editorSettings, !!rp.host?.compact),
		};
	}

	/** The text of the view on screen: the WYSIWYG editor serializes its document, the source view is the text itself */
	get viewContent(): string {
		return this.sourceMode ? this.code?.content : this.markdown?.content;
	}

	_onKeyDown = (e: KeyboardEvent) => {
		if (this.options?.toolbar?.save?.enabled && !this.options?.readOnly && e.key == 's' && e.ctrlKey) {
			e.preventDefault();
			this.save();
		}
	}

	/** An editor setting changed (gear menu): the source view gets the new Monaco options */
	private _onSettingsChange = (e: SettingsChangeEvent) => {
		if (!isEditorSetting(e.detail.id)) return;
		this._editorSettings = getEditorSettings();
		if (this.code) this.code.options = this.monacoOptions;
	};

	connectedCallback(): void {
		super.connectedCallback();
		this.addEventListener('keydown', this._onKeyDown);
		rp.settings.addEventListener('change', this._onSettingsChange);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		this.removeEventListener('keydown', this._onKeyDown);
		rp.settings.removeEventListener('change', this._onSettingsChange);
	}

	/** Puts the text into the view on screen; the WYSIWYG editor renders anew, the source view keeps its state when nothing changed */
	private setViewContent(content: string) {
		if (this.sourceMode) {
			if (this.code && this.code.content !== (content ?? '')) this.code.content = content;
		} else if (this.markdown) {
			this.markdown.content = content;
		}
	}

	async load() {
		if (this._loading) return;
		this._loading = true;
		this.loader.show(this);
		if (this._content) {
			this.setViewContent(this._content);
		} else {
			this._content = await this.adapter.getContent();
			if (this._content !== null) {
				this._content = fixMarkdown(this._content);
				this.setViewContent(this._content);
			}
		}
		this.markdown?.requestUpdate();
		this._loading = false;
		this.loader.hide();
	}

	async loadDiff() {
		if (this._loading) return;
		this._loading = true;
		const content = await this.adapter.getHistoryContent(this.diffIdz);
		this.diffEditor.language = this.options.language;
		this.diffEditor.originalContent = this._content ?? '';
		this.diffEditor.modifiedContent = content ?? '';
		this._loading = false;
	}

	async willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('adapter') && this.adapter) {
			this.adapter.editor = this;
			this.options = await this.adapter.getOptions();
			if (this.markdown) this.markdown.readOnly = this.options.readOnly || false;
			this.spellcheck = this.options.spellcheck || false;
			if (this.markdown) this.markdown.spellcheck = this.spellcheck;
			this.toolbarItems = await this.createToolbarItems();
			this.history = !!this.options.history;
			if (this.history) {
				this.historyAdapter = new MarkdownHistoryAdapter(this.adapter);
				this.currentLiveItem = await this.adapter.getCurrentItem();
			}
		}
	}

	async updated(props: Map<PropertyKey, unknown>) {
		let loadDiff = false;
		let load = false;

		if (props.has('adapter') && this.adapter) {
			this.options = await this.adapter.getOptions();
			this.adapter.attach(this);
			if (!this.diffIdz) load = true;
		}

		if (props.has('diffIdz')) {
			loadDiff = !!this.diffIdz;
			load = load || !loadDiff;
		}

		if (loadDiff) this.loadDiff();
		else if (load) this.load();
	}

	async createToolbarItems() {
		const options = this.options;
		if (!options.toolbar) return [];
		const items: ToolbarItem[] = [];
		if (options.toolbar.refresh?.enabled) items.push({
			type: 'button',
			id: 'refresh',
			text: options.toolbar.refresh.text,
			color: options.toolbar.refresh.color,
			icon: options.toolbar.refresh.icon
		});

		if (options.toolbar.save?.enabled) items.push({
			type: 'button',
			id: 'save',
			text: options.toolbar.save.text,
			color: options.toolbar.save.color,
			icon: options.toolbar.save.icon
		});

		if (options.toolbar.discard?.enabled) items.push({
			type: 'button',
			id: 'discard',
			text: options.toolbar.discard.text,
			color: options.toolbar.discard.color,
			icon: options.toolbar.discard.icon
		});

		if (options.toolbar.spellcheck?.enabled) items.push({
			type: 'togglebutton',
			id: 'spellcheck',
			title: options.toolbar.spellcheck.title,
			text: options.toolbar.spellcheck.text,
			color: options.toolbar.spellcheck.color,
			icon: options.toolbar.spellcheck.icon,
			value: this.spellcheck
		});

		if (options.toolbar.source?.enabled) items.push({
			type: 'togglebutton',
			id: 'source',
			title: options.toolbar.source.title,
			text: options.toolbar.source.text,
			color: options.toolbar.source.color,
			icon: options.toolbar.source.icon,
			value: this.sourceMode
		});

		return items;
	}

	async save() {
		if(!this._editing) return;
		if (!this._saving) {
			this._saving = true;
			this.loader.show(this);
			let content = this.viewContent;
			// upload all attachments and replace url for markdown images
			for (const img of this.markdownImages) {
				if(!content.includes(img.url.toString())) continue;
				const fileName = await this.adapter.uploadFile(img.file);
				// images are saved like this: ![img ratio](url "title")
				content = content.replace(img.url.toString(), fileName);
			}
			await this.adapter.save(content);
			this._content = content;
			this.setViewContent(content);
			this.markdownImages = [];
			this._saving = false;
			this._editing = false;
			this.loader.hide();
			if (this._pendingSave) {
				this._pendingSave = false;
				this.save();
			}
			this.historyAdapter?.invalidateItems();
			this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		} else {
			this._pendingSave = true;
		}
	}

	async discard(force: boolean = false) {
		if(!this._editing && !force) return;
		this.loader.show(this);
		this._content = await this.adapter.getContent();
		this.setViewContent(this._content);
		this._editing = false;
		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		this.loader.hide();
	}

	async refresh() {
		if (this._editing) {
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: msg('Discard changes and refresh?'),
			});
			if (command?.id == 'Y') {
				this.discard();
			} else {
				return;
			}
		}
		this.discard(true);
	}

	setSpellcheck(enabled: boolean) {
		this.spellcheck = enabled;
		if (this.markdown) this.markdown.spellcheck = enabled;
	}

	/** Switches between the WYSIWYG editor and the markdown source; the text on screen, edits included, moves along */
	async setSourceMode(enabled: boolean) {
		if (enabled === this.sourceMode) return;
		const content = this.viewContent ?? this._content;
		this.sourceMode = enabled;
		await this.updateComplete;
		this.setViewContent(content);
		this.markdown?.requestUpdate();
	}

	ready() {}

	edit(e: events.ChangeEvent = null) {
		this.dispatchEvent(new CustomEvent('edit', {detail: e, bubbles: true, composed: true}));
		this._editing = true;
	}

	async _onToolbarAction(e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'save': this.save(); break;
			case 'discard': this.discard(); break;
			case 'refresh': this.refresh(); break;
			case 'spellcheck': this.setSpellcheck(e.action.value as boolean); break;
			case 'source': this.setSourceMode(!!e.action.value); break;
		}
	}

	// image upload from markdown editor
	_onImgUpload = async (file: File): Promise<string> => {
		if (this.options?.disableImgUpload) {
			alert('Image upload is disabled. Please use link to external images instead.');
			return null;
		}
		const url = URL.createObjectURL(file);
		this.markdownImages.push({url, file});
		return url;
	}

	_onLink = (e: MarkdownLinkEvent) => {
		e.preventDefault();
		this.adapter.onLink(e);
	}

	_onChange = (e: events.ChangeEvent) => {
		if (this.markdown.content === this._content) return;
		this.edit(e);
	}

	_onCodeChange = () => {
		if (this.code.content === this._content) return;
		this.edit();
	}

	_onReady = () => {
		this.ready();
	}

	_modifyImageUrl = (url: string) => {
		return this.adapter.modifyImageUrl(url);
	}

	_modifyLinkUrl = (href: string) => {
		return this.adapter.modifyLinkUrl(href);
	}

	_onDiffChange = () => {
		this._content = this.diffEditor?.modifiedContent;
		this.edit();
	}

	_onHistoryChange = async (e: TreeEvent) => {
		const idz = e.node?.key;
		if (!this.diffIdz) {
			this._content = this.viewContent;
		}
		this.diffIdz = idz ? parseInt(idz) : null;
	}

	render = () => html`
		<og-overlay defaultLoader></og-overlay>
		${this.options?.readOnly ? '' : html`
			<div class="controls">
				<og-toolbar
					.items="${this.toolbarItems}"
					@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
				</og-toolbar>

				<div id="empty"></div>

				${this.history ? html`
					<div id="history">
						<og-tree-dropdown
							cross
							color="gray"
							.placeholder="${this.currentLiveItem ? this.historyAdapter.formatItem(this.currentLiveItem) : ''}"
							id="ddHistory"
							.adapter="${this.historyAdapter}"
							@change="${this._onHistoryChange}">
						</og-tree-dropdown>
					</div>
				` : ''}

				${this.sourceMode ? html`
					<rp-editor-settings-menu language="markdown"></rp-editor-settings-menu>
				` : ''}
			</div>
		`}

		<div class="body">
			${guard([this.adapter, this.diffIdz, this.sourceMode], () => this.diffIdz ? html`
				<og-code-diff
					@code.change="${this._onDiffChange}">
				</og-code-diff>
			` : this.sourceMode ? html`
				<og-code
					.language="${'markdown'}"
					.options="${this.monacoOptions}"
					@code.change="${this._onCodeChange}">
				</og-code>
			` : html`
				<og-markdown-editor
					maxContentWidth="2000"
					.onUpload="${this._onImgUpload}"
					.modifyImageUrl="${this._modifyImageUrl}"
					.modifyLinkUrl="${this._modifyLinkUrl}"
					@markdown.link="${this._onLink}"
					@markdown.ready="${this._onReady}"
					@change="${this._onChange}">
				</og-markdown-editor>
			`)}
		</div>
	`;

	layout() {
		if (this.sourceMode) this.code?.layout();
		else this.markdown?.layout();
	}

}
